import { BASE_URL, FETCH_TRANSACTION, FETCH_TRANSACTION_SETTINGS_CODE } from "../config";
import { getSessionId } from "../utils/SessionHandler";

const root = "api/v1/organisation/purchase/comparisons";

// Do not use apiHelper's error-as-data or GET cache for live allocations.
async function request(path, { method = "GET", body, signal } = {}) {
  const response = await fetch(`${BASE_URL.replace(/\/$/, "")}/${path}`, {
    method, signal, cache: "no-store",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getSessionId()}` },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.message || data?.error?.message || data?.description || data?.error?.description || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  if (data === null) throw new Error("Invalid purchase comparison response. Reload before continuing.");
  return data;
}

function comparison(data) {
  if (!Number.isSafeInteger(data?.id) || !["draft", "ordered"].includes(data.status) || !Array.isArray(data.snapshot?.lines)) {
    throw new Error("Invalid comparison details. Reload before continuing.");
  }
  if (data.status === "ordered" && !Array.isArray(data.orders)) throw new Error("Invalid saved order response.");
  return data;
}

export const purchaseComparisonsApi = {
  list: async (page = 1, signal) => {
    const data = await request(`${root}?page=${page}`, { signal });
    if (!Array.isArray(data.data) || !Number.isFinite(data.total)) throw new Error("Invalid comparison history response.");
    return data;
  },
  get: async (id, signal) => comparison(await request(`${root}/${encodeURIComponent(id)}`, { signal })),
  create: async ({ indent_code, indent_id, enquiry_code, quotation_code, order_code }) => comparison(await request(root, {
    method: "POST", body: { indent_code, indent_id, enquiry_code, quotation_code, order_code },
  })),
  orders: async (id, selections) => {
    const data = await request(`${root}/${encodeURIComponent(id)}/orders`, {
      method: "POST",
      body: { selections: selections.map(({ quotation_id, quotation_serial, quantity }) => ({ quotation_id, quotation_serial, quantity })) },
    });
    if (!Array.isArray(data) || !data.length || data.some((order) => !order.tran_code || !Number.isSafeInteger(order.tran_id))) {
      throw new Error("Invalid order response. Reload the comparison to check whether orders were saved.");
    }
    return data;
  },
  settings: (code, signal) => request(`${FETCH_TRANSACTION_SETTINGS_CODE}${encodeURIComponent(code)}`, { signal }),
  indents: async (code, signal) => {
    // This handler currently returns the full TransactionList, despite page/count.
    // Search/pagination in the picker is local; do not invent a total or next page.
    const data = await request(`${FETCH_TRANSACTION}${encodeURIComponent(code)}?page=1&count=20`, { signal });
    if (!data.TransactionSettings || (data.TransactionList !== null && !Array.isArray(data.TransactionList))) {
      throw new Error("Invalid indent list response.");
    }
    return { ...data, TransactionList: data.TransactionList || [] };
  },
};